/* ==========================================================================
   Azhara search — predictive overlay + client-side fragrance index
   - Merges native /search/suggest.json with a Storefront API index of
     'fragrance' metaobjects (name, brand, notes, linked product).
   - Exposes window.AzharaSearch for the /search results page augmentation.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__azharaSearchLoaded) return;
  window.__azharaSearchLoaded = true;

  var CONFIG = window.AzharaSearchConfig || {};
  var SHOP_DOMAIN = CONFIG.domain || 'hypixb-gi.myshopify.com';
  var STOREFRONT_API = 'https://' + SHOP_DOMAIN + '/api/2025-01/graphql.json';
  var STOREFRONT_TOKEN = '8005ba494022cde89d164f2f933a7ac8';
  var INDEX_KEY = 'azhara-frag-index-v1';
  var RECENT_KEY = 'azhara-recent-searches';
  var INDEX_TTL = 86400000; /* 24h */
  var POPULAR = ['Vanilla', 'Oud', 'Dior', 'Tom Ford'];

  /* ---------- helpers ---------- */

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* diacritic-insensitive fold: 'Maréllo' -> 'marello' */
  function fold(s) {
    return String(s == null ? '' : s)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  function money(n) {
    var v = Math.round(parseFloat(n));
    if (isNaN(v)) return '';
    return '₹' + v.toLocaleString('en-IN');
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments, self = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, ms);
    };
  }

  /* ---------- fragrance index ---------- */

  var indexData = null;      /* prepared records */
  var indexPromise = null;
  var productMap = {};       /* product handle -> record */

  function prepare(records) {
    productMap = {};
    for (var i = 0; i < records.length; i++) {
      var r = records[i];
      r._n = fold(r.n);
      r._b = fold(r.b);
      r._hay = r._n + ' ' + r._b + ' ' + fold(r.notes);
      if (r.p && r.p.h) productMap[r.p.h] = r;
    }
    return records;
  }

  function fetchIndex() {
    var all = [];
    var cursor = null;

    function page() {
      var after = cursor ? ', after: "' + cursor + '"' : '';
      var query = '{ metaobjects(type: "fragrance", first: 250' + after + ') { edges { node { handle fields { key value } brandRef: field(key: "brand") { reference { ... on Metaobject { fields { key value } } } } linkedProduct: field(key: "linked_product") { reference { ... on Product { title handle availableForSale featuredImage { url } priceRange { minVariantPrice { amount } } } } } } cursor } pageInfo { hasNextPage } } }';

      return fetch(STOREFRONT_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
        },
        body: JSON.stringify({ query: query })
      })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (!data.data || !data.data.metaobjects) {
            throw new Error('[AzharaSearch] index fetch failed');
          }
          var edges = data.data.metaobjects.edges;
          for (var i = 0; i < edges.length; i++) {
            var node = edges[i].node;
            var f = {};
            node.fields.forEach(function (field) { f[field.key] = field.value; });

            var brandName = '';
            if (node.brandRef && node.brandRef.reference) {
              node.brandRef.reference.fields.forEach(function (bf) {
                if (bf.key === 'name') brandName = bf.value || '';
              });
            }

            var prod = node.linkedProduct && node.linkedProduct.reference ? node.linkedProduct.reference : null;

            all.push({
              n: f.name || '',
              b: brandName,
              g: f.gender || '',
              notes: [f.top_notes, f.middle_notes, f.base_notes].filter(Boolean).join(' '),
              h: node.handle,
              p: prod ? {
                t: prod.title,
                h: prod.handle,
                img: prod.featuredImage ? prod.featuredImage.url : '',
                price: prod.priceRange ? prod.priceRange.minVariantPrice.amount : '',
                a: prod.availableForSale ? 1 : 0
              } : null,
              avail: f.is_available === 'true'
            });
          }
          if (data.data.metaobjects.pageInfo.hasNextPage && edges.length > 0) {
            cursor = edges[edges.length - 1].cursor;
            return page();
          }
          return all;
        });
    }

    return page();
  }

  function storeIndex(recs) {
    try {
      localStorage.setItem(INDEX_KEY, JSON.stringify({ ts: Date.now(), data: recs }));
    } catch (e) { /* storage full or unavailable: index stays in memory */ }
  }

  function refreshInBackground() {
    fetchIndex().then(function (recs) {
      indexData = prepare(recs);
      storeIndex(recs);
    }).catch(function () { /* keep stale index */ });
  }

  function ensureIndex() {
    if (indexPromise) return indexPromise;

    var cached = null;
    try { cached = JSON.parse(localStorage.getItem(INDEX_KEY)); } catch (e) { cached = null; }

    if (cached && cached.data && cached.data.length) {
      indexData = prepare(cached.data);
      indexPromise = Promise.resolve(indexData);
      if (Date.now() - (cached.ts || 0) > INDEX_TTL) refreshInBackground();
      return indexPromise;
    }

    indexPromise = fetchIndex().then(function (recs) {
      indexData = prepare(recs);
      storeIndex(recs);
      return indexData;
    }).catch(function (err) {
      indexPromise = null; /* allow retry on next call */
      throw err;
    });
    return indexPromise;
  }

  /* tokenized, order-independent, diacritic-insensitive matcher */
  function matchIndex(q, limit) {
    if (!indexData) return [];
    var tokens = fold(q).split(/\s+/).filter(Boolean);
    if (!tokens.length) return [];

    var out = [];
    for (var i = 0; i < indexData.length; i++) {
      var r = indexData[i];
      var score = 0;
      var ok = true;
      for (var t = 0; t < tokens.length; t++) {
        var tok = tokens[t];
        if (r._n.indexOf(tok) !== -1) score += 4;
        else if (r._b.indexOf(tok) !== -1) score += 3;
        else if (r._hay.indexOf(tok) !== -1) score += 1;
        else { ok = false; break; }
      }
      if (ok) out.push({ r: r, s: score });
    }
    out.sort(function (a, b) {
      if (b.s !== a.s) return b.s - a.s;
      var ap = a.r.p ? 1 : 0, bp = b.r.p ? 1 : 0;
      if (bp !== ap) return bp - ap;
      return a.r.n.localeCompare(b.r.n);
    });
    var recs = out.map(function (o) { return o.r; });
    return limit ? recs.slice(0, limit) : recs;
  }

  /* 'Inspired by' label: prefix brand unless the name already contains it */
  function inspiredLabel(rec) {
    if (!rec) return '';
    if (rec.b && fold(rec.n).indexOf(fold(rec.b)) === -1) return rec.b + ' ' + rec.n;
    return rec.n;
  }

  /* ---------- native predictive search ---------- */

  function fetchSuggest(q) {
    var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product,page,collection&resources[limit]=6';
    return fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var res = (data.resources && data.resources.results) || {};
        return {
          products: (res.products || []).map(function (p) {
            return {
              title: p.title,
              handle: p.handle,
              url: p.url,
              img: p.image || (p.featured_image && p.featured_image.url) || '',
              price: p.price,
              available: p.available !== false
            };
          }),
          pages: (res.pages || []).map(function (p) { return { title: p.title, url: p.url, kind: 'Page' }; }),
          collections: (res.collections || []).map(function (c) { return { title: c.title, url: c.url, kind: 'Collection' }; })
        };
      });
  }

  /* ---------- recent searches ---------- */

  function getRecent() {
    try {
      var v = JSON.parse(localStorage.getItem(RECENT_KEY));
      return Array.isArray(v) ? v : [];
    } catch (e) { return []; }
  }

  function addRecent(q) {
    q = String(q || '').trim();
    if (!q) return;
    var list = getRecent().filter(function (item) { return fold(item) !== fold(q); });
    list.unshift(q);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 5))); } catch (e) { /* noop */ }
  }

  /* ---------- shared render bits ---------- */

  function thumbHtml(src, alt) {
    if (src) {
      var sep = src.indexOf('?') === -1 ? '?' : '&';
      return '<img class="azs-thumb" src="' + esc(src) + sep + 'width=120" alt="' + esc(alt) + '" loading="lazy" width="46" height="46">';
    }
    return '<div class="azs-thumb azs-thumb--empty"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg></div>';
  }

  function productItemHtml(p) {
    var sub = p.inspired ? '<div class="azs-item-sub">Inspired by ' + esc(p.inspired) + '</div>' : '';
    var right = p.available
      ? '<span class="azs-item-price">' + esc(money(p.price)) + '</span>'
      : '<span class="azs-item-oos">Sold out</span>';
    return '<a class="azs-item" href="' + esc(p.url) + '">' +
      thumbHtml(p.img, p.title) +
      '<span class="azs-item-main"><span class="azs-item-title">' + esc(p.title) + '</span>' + sub + '</span>' +
      right +
      '</a>';
  }

  function libraryItemHtml(rec) {
    var sub = rec.b ? '<div class="azs-item-sub">' + esc(rec.b) + (rec.g ? ' · ' + esc(rec.g) : '') + '</div>' : '';
    var shop = rec.p
      ? '<a class="azs-shop" href="/products/' + esc(rec.p.h) + '">Shop the oil</a>'
      : '';
    return '<div class="azs-row">' +
      '<a class="azs-item" href="/pages/fragrance/' + esc(rec.h) + '">' +
      '<span class="azs-item-main"><span class="azs-item-title azs-lib-name">' + esc(rec.n) + '</span>' + sub + '</span>' +
      '</a>' + shop +
      '</div>';
  }

  /* ---------- public API (used by templates/search.liquid too) ---------- */

  window.AzharaSearch = {
    ensureIndex: ensureIndex,
    match: matchIndex,
    inspiredLabel: inspiredLabel,
    productForHandle: function (handle) { return productMap[handle] || null; },
    fold: fold,
    esc: esc,
    money: money,
    getRecent: getRecent,
    addRecent: addRecent,
    POPULAR: POPULAR
  };

  /* ======================================================================
     Overlay wiring (only when the header rendered the overlay markup)
     ====================================================================== */

  var overlay = document.getElementById('azs-overlay');

  function init() {
    overlay = overlay || document.getElementById('azs-overlay');
    if (!overlay) return;

    /* escape the fixed header's stacking context (same pattern as pcd drawers) */
    if (overlay.parentElement !== document.body) document.body.appendChild(overlay);

    var panel = overlay.querySelector('.azs-panel');
    var form = overlay.querySelector('.azs-form');
    var input = overlay.querySelector('.azs-input');
    var body = overlay.querySelector('[data-azs-body]');
    var footer = overlay.querySelector('[data-azs-all]');

    var state = { seq: 0, q: '', suggest: null, activeIdx: -1 };
    var isOpen = false;

    function open() {
      if (isOpen) return;
      isOpen = true;
      overlay.hidden = false;
      /* two frames so the transition runs after unhide */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { overlay.classList.add('is-open'); });
      });
      document.body.style.overflow = 'hidden';
      ensureIndex().then(function () {
        /* index arrived after opening: refresh current results */
        if (isOpen && state.q) render();
      }).catch(function () { /* suggest.json still works */ });
      renderDefault();
      input.value = '';
      state.q = '';
      input.focus();
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      overlay.classList.remove('is-open');
      overlay.hidden = true;
      document.body.style.overflow = '';
      state.seq++;
      state.activeIdx = -1;
    }

    function chipsHtml(items) {
      return '<div class="azs-chips">' + items.map(function (c) {
        return '<button type="button" class="azs-chip" data-azs-chip="' + esc(c) + '">' + esc(c) + '</button>';
      }).join('') + '</div>';
    }

    function renderDefault() {
      state.activeIdx = -1;
      footer.hidden = true;
      var recent = getRecent();
      var html = '';
      if (recent.length) {
        html += '<div class="azs-group"><h3 class="azs-group-title">Recent searches</h3>' + chipsHtml(recent) + '</div>';
      }
      html += '<div class="azs-group"><h3 class="azs-group-title">Popular</h3>' + chipsHtml(POPULAR) + '</div>';
      body.innerHTML = html;
    }

    function renderLoading() {
      body.innerHTML = '<div class="azs-group"><div class="azs-skel"></div><div class="azs-skel"></div><div class="azs-skel"></div></div>';
    }

    function render() {
      if (!state.q) { renderDefault(); return; }
      state.activeIdx = -1;

      var fragMatches = indexData ? matchIndex(state.q, 24) : [];
      var suggest = state.suggest;

      /* merge products: native hits + linked products from the index */
      var products = [];
      var seen = {};
      if (suggest) {
        suggest.products.forEach(function (p) {
          var rec = productMap[p.handle];
          p.inspired = rec ? inspiredLabel(rec) : null;
          products.push(p);
          seen[p.handle] = true;
        });
      }
      fragMatches.forEach(function (rec) {
        if (!rec.p || seen[rec.p.h]) return;
        seen[rec.p.h] = true;
        products.push({
          title: rec.p.t,
          handle: rec.p.h,
          url: '/products/' + rec.p.h,
          img: rec.p.img,
          price: rec.p.price,
          available: !!rec.p.a,
          inspired: inspiredLabel(rec)
        });
      });
      /* available first, stable */
      products = products.filter(function (p) { return p.available; })
        .concat(products.filter(function (p) { return !p.available; }))
        .slice(0, 6);

      var library = fragMatches.slice(0, 6);
      var other = suggest ? suggest.collections.concat(suggest.pages).slice(0, 4) : [];

      var html = '';
      if (products.length) {
        html += '<div class="azs-group"><h3 class="azs-group-title">Products</h3>' +
          products.map(productItemHtml).join('') + '</div>';
      }
      if (library.length) {
        html += '<div class="azs-group"><h3 class="azs-group-title">Fragrance library</h3>' +
          library.map(libraryItemHtml).join('') + '</div>';
      }
      if (other.length) {
        html += '<div class="azs-group"><h3 class="azs-group-title">Collections &amp; pages</h3>' +
          other.map(function (o) {
            return '<a class="azs-item" href="' + esc(o.url) + '">' +
              '<span class="azs-item-main"><span class="azs-item-title">' + esc(o.title) + '</span>' +
              '<div class="azs-item-sub">' + esc(o.kind) + '</div></span></a>';
          }).join('') + '</div>';
      }

      if (!html) {
        if (suggest === null || (!indexData && !suggest)) {
          renderLoading();
          footer.hidden = true;
          return;
        }
        html = '<div class="azs-empty">' +
          '<p class="azs-empty-text">No matches for &quot;' + esc(state.q) + '&quot;</p>' +
          chipsHtml(POPULAR) +
          '<a class="azs-link" href="/pages/all-fragrances">Browse the fragrance library</a>' +
          '</div>';
        body.innerHTML = html;
        footer.hidden = true;
        return;
      }

      body.innerHTML = html;
      footer.href = '/search?q=' + encodeURIComponent(state.q);
      footer.hidden = false;
    }

    function runSearch(q) {
      var seq = ++state.seq;
      state.q = q;
      state.suggest = null;
      render(); /* immediate: index-side results or skeleton */
      fetchSuggest(q).then(function (r) {
        if (seq !== state.seq) return;
        state.suggest = r;
        render();
      }).catch(function () {
        if (seq !== state.seq) return;
        state.suggest = { products: [], pages: [], collections: [] };
        render();
      });
    }

    var onType = debounce(function () {
      var q = input.value.trim();
      if (q === state.q) return;
      if (!q) { state.seq++; state.q = ''; renderDefault(); return; }
      runSearch(q);
    }, 200);

    input.addEventListener('input', onType);

    /* keyboard: arrows + enter, esc closes */
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      var items = overlay.querySelectorAll('.azs-item');
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (!items.length) return;
        var dir = e.key === 'ArrowDown' ? 1 : -1;
        state.activeIdx = (state.activeIdx + dir + items.length) % items.length;
        items.forEach(function (el, i) { el.classList.toggle('is-active', i === state.activeIdx); });
        items[state.activeIdx].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter') {
        if (state.activeIdx >= 0 && items[state.activeIdx]) {
          e.preventDefault();
          addRecent(state.q);
          items[state.activeIdx].click();
        }
        /* else: native form submit -> /search?q= */
      }
    });

    overlay.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });

    form.addEventListener('submit', function (e) {
      var q = input.value.trim();
      if (!q) {
        /* empty query: just close, don't navigate */
        e.preventDefault();
        close();
        return;
      }
      addRecent(q);
    });

    /* delegated clicks inside the overlay */
    overlay.addEventListener('click', function (e) {
      if (e.target.closest('[data-azs-close]')) { close(); return; }

      var chip = e.target.closest('[data-azs-chip]');
      if (chip) {
        var q = chip.getAttribute('data-azs-chip');
        input.value = q;
        input.focus();
        runSearch(q);
        return;
      }

      var link = e.target.closest('a');
      if (link && panel.contains(link)) addRecent(state.q);
    });

    /* open triggers: header icon + mobile drawer search link + any [data-azs-open] */
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('.header__search-link, .mobile-nav__link[href="/search"], [data-azs-open]');
      if (!trigger) return;
      e.preventDefault();
      var drawer = document.getElementById('mobile-drawer');
      var drawerOverlay = document.getElementById('mobile-drawer-overlay');
      if (drawer) drawer.classList.remove('is-active');
      if (drawerOverlay) drawerOverlay.classList.remove('is-active');
      open();
    });

    window.AzharaSearch.open = open;
    window.AzharaSearch.close = close;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
